import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * PassportLogout Component
 *
 * This component handles the redirect after logging out from Immutable Passport.
 * It clears any remaining session data and redirects the user back to the application.
 * It also includes safeguards against infinite redirects and reload loops.
 */
export default function PassportLogout() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Check if we're already in a logout process to prevent loops
    const isLogoutInProgress = sessionStorage.getItem('passport_logout_in_progress');

    // If we detect we're in a potential loop, abort the process
    if (isLogoutInProgress === 'completed') {
      console.warn('Detected multiple logout redirects, aborting to prevent loops');
      setIsProcessing(false);
      setError('Logout process was interrupted to prevent a redirect loop. Please try again.');

      // Clear the flag and redirect to home after a short delay
      setTimeout(() => {
        sessionStorage.removeItem('passport_logout_in_progress');
        navigate('/', { replace: true });
      }, 2000);

      return;
    }

    // Start a countdown to automatically redirect if the logout takes too long
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          // Force redirect to home if countdown reaches 0
          console.log('Logout countdown reached 0, forcing redirect to home');

          // Clear all Passport-related data
          localStorage.removeItem('passport_user_info');
          localStorage.removeItem('passport_address');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('wallet_provider');
          localStorage.removeItem('wallet_reconnect_requested');
          localStorage.removeItem('wallet_connected');

          // Clear the flag
          sessionStorage.removeItem('passport_logout_in_progress');

          // Redirect to home
          navigate('/', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const performLogout = async () => {
      try {
        // Set a flag to indicate we're in the logout process
        sessionStorage.setItem('passport_logout_in_progress', 'started');

        // Clear any remaining session data
        try {
          await logout();
        } catch (logoutError) {
          console.error('Error during logout:', logoutError);
          // Continue with cleanup even if logout fails
        }

        // Mark the logout as completed
        sessionStorage.setItem('passport_logout_in_progress', 'completed');

        // Set a timeout to clear the flag
        setTimeout(() => {
          sessionStorage.removeItem('passport_logout_in_progress');
        }, 5000);

        // Redirect to home page with replace to avoid browser history issues
        navigate('/', { replace: true });
      } catch (error) {
        console.error('Error during Passport logout process:', error);
        setError('An error occurred during logout. Please try again.');
        setIsProcessing(false);

        // Clear the flag
        sessionStorage.removeItem('passport_logout_in_progress');
      }
    };

    performLogout();

    // Cleanup function
    return () => {
      clearInterval(countdownInterval);
      // Ensure we clean up the flag if the component unmounts
      setTimeout(() => {
        sessionStorage.removeItem('passport_logout_in_progress');
      }, 1000);
    };
  }, [navigate, logout]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-dark">
        <div className="p-6 bg-dark-lighter rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-white mb-4">Logout Error</h2>
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => {
              // Clear all Passport-related data before navigating
              localStorage.removeItem('passport_user_info');
              localStorage.removeItem('passport_address');
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('wallet_provider');
              localStorage.removeItem('wallet_reconnect_requested');
              localStorage.removeItem('wallet_connected');

              // Navigate to home
              navigate('/');
            }}
            className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-dark">
      <div className="p-6 bg-dark-lighter rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold text-white mb-4">Logging Out</h2>
        <p className="text-gray-300 mb-4">Please wait while we complete the logout process...</p>
        <p className="text-gray-400 text-sm mb-4">Redirecting in {countdown} seconds</p>
        <div className="w-full flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => {
              // Clear all Passport-related data before navigating
              localStorage.removeItem('passport_user_info');
              localStorage.removeItem('passport_address');
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('wallet_provider');
              localStorage.removeItem('wallet_reconnect_requested');
              localStorage.removeItem('wallet_connected');

              // Navigate to home
              navigate('/');
            }}
            className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors"
          >
            Return to Home Now
          </button>
        </div>
      </div>
    </div>
  );
}
